import React, { FC, Fragment, useContext, useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { Grid } from '@mui/material';
import {
    workFlowTemplate,
    addStepTemplate,
    deleteStepTemplate,
    moveStepTemplate,
    defaultSampleStepTemplate,
    updateStepTemplate,
} from 'apollo/templates/workFlow';
import { WorkFlow, Steps, DefaultSampleStep } from 'models/workflow-step';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import WorkflowSteps from './steps/workflow-steps.components';
import DefaultSteps from './default-step/default-step.components';
import SubcontractorRules from './subcontractor-rules/subcontractor-rules.component';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { useDepartments } from 'context/departments-context';
import { Department } from 'models/organization';
import { AppToasterContext } from 'components/shared/app-toaster/app-toaster-context';
import { useTranslation } from 'react-i18next';

const WorkflowSettings: FC = () => {
    const { t: translate } = useTranslation(['actions-materials']);
    const [workflow, setWorkflow] = useState<WorkFlow>({} as WorkFlow);
    const { showBoundary } = useDenthubErrorBoundary();
    const { departments } = useDepartments();
    const { setAppToasterContent } = useContext(AppToasterContext);

    useQuery(workFlowTemplate, {
        variables: {},
        onCompleted: ({ workFlow }) => {
            setWorkflow(workFlow);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const [addStep] = useMutation(addStepTemplate, {
        onError(error) {
            showBoundary(error);
        },
        onCompleted: ({ addStep }) => {
            setWorkflow(addStep);
        },
    });

    const updateStoredOrgWorFlow = (workFlow: WorkFlow) => {
        const currentOrganization = currentOrgVar();
        currentOrgVar({ ...currentOrganization, workFlow });
    };

    const [updateStep] = useMutation(updateStepTemplate, {
        onError(error) {
            showBoundary(error);
        },
        onCompleted: ({ updateStep: workFlow }) => {
            updateStoredOrgWorFlow(workFlow);
            setWorkflow(workFlow);
        },
    });

    const [deleteStep] = useMutation(deleteStepTemplate, {
        onError(error) {
            showBoundary(error);
        },
        onCompleted: ({ deleteStep: workFlow }) => {
            updateStoredOrgWorFlow(workFlow);
            setWorkflow(workFlow);
        },
    });

    const [moveStep] = useMutation(moveStepTemplate, {
        onError(error) {
            showBoundary(error);
        },
        onCompleted: ({ moveStep: workFlow }) => {
            updateStoredOrgWorFlow(workFlow);
            setWorkflow(workFlow);
        },
    });

    const [sampleStep] = useMutation(defaultSampleStepTemplate, {
        onError(error) {
            showBoundary(error);
        },
        onCompleted: ({ defaultSampleStep: workFlow }) => {
            updateStoredOrgWorFlow(workFlow);
            setWorkflow(workFlow);
        },
    });

    const onStepAdd = async (step: Steps) => {
        const defaultExtUsers = step.defaultExtUsers.map((user) => user._id);
        await addStep({
            variables: {
                name: step.name,
                description: step.description,
                maxHourInStep: step.maxHourInStep,
                defaultExtUsers: defaultExtUsers,
                useMillingDeadline: step.useMillingDeadline || false,
                millingDeadlineDays: step.millingDeadlineDays || null,
                initiatesSubcontract: step.initiatesSubcontract || false,
            },
        });
    };

    const onStepUpdate = async (step: Steps) => {
        const defaultExtUsers = step.defaultExtUsers.map((user) => user._id);
        await updateStep({
            variables: {
                stepId: step._id,
                name: step.name,
                description: step.description,
                maxHourInStep: step.maxHourInStep,
                defaultExtUsers: defaultExtUsers,
                useMillingDeadline: step.useMillingDeadline || false,
                millingDeadlineDays: step.millingDeadlineDays || null,
                initiatesSubcontract: step.initiatesSubcontract || false,
            },
        });
    };
    const onStepDelete = async (stepId: string) => {
        const isStepInUse = departments.some((dept: Department) =>
            dept.workflowSteps.map((s) => s._id).includes(stepId),
        );
        if (isStepInUse) {
            setAppToasterContent({
                isOpen: true,
                severity: 'error',
                message: translate('actions-materials:CANNOT_DELETE_STEP_IN_USE'),
            });
            return;
        } else {
            await deleteStep({
                variables: { stepId: stepId },
            });
        }
    };

    const onStepMove = async (currentIndex: number, newIndex: number) => {
        await moveStep({
            variables: { currentIndex: currentIndex, newIndex: newIndex },
        });
    };

    const onDefaultStepChange = async (defaultSampleStep: DefaultSampleStep) => {
        await sampleStep({
            variables: {
                defaultNoSample: defaultSampleStep.defaultNoSample,
                defaultPhysicalSample: defaultSampleStep.defaultPhysicalSample,
                defaultDigitalSample: defaultSampleStep.defaultDigitalSample,
            },
        });
    };

    return (
        <Fragment>
            <Grid container mb={4}>
                <Grid item xs={12}>
                    <WorkflowSteps
                        workflow={workflow}
                        onStepAdd={onStepAdd}
                        onStepUpdate={onStepUpdate}
                        onStepDelete={onStepDelete}
                        onStepMove={onStepMove}
                    />
                </Grid>
            </Grid>
            <DefaultSteps workflow={workflow} onDefaultStepChange={onDefaultStepChange} />
            <SubcontractorRules steps={workflow.steps || []} />
        </Fragment>
    );
};

export default WorkflowSettings;
